"use client";

import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "../../../convex/_generated/api";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import {
  Loader2,
  CheckCircle2,
  Calendar,
  Clock,
  MapPin,
  PartyPopper,
  AlertTriangle,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Id } from "../../../convex/_generated/dataModel";
import { formatEventDate, formatTimeTo12Hour } from "@/lib/events";
import { splitRsvpFullName } from "@/lib/rsvp-name";
import { analytics, getLeadAttribution } from "@/lib/analytics";
import { motion, AnimatePresence } from "framer-motion";

interface RSVPModalProps {
  event: {
    _id: Id<"events">;
    title: string;
    date: string;
    startTime: string;
    locationId?: string;
    eventType?: string;
    eventTypeOther?: string;
  };
  maxPartySize?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Success-screen copy for the public RSVP dialog.
 *
 * Pure so a test can assert the outcomes without mounting the dialog: already
 * registered, a confirmation re-queued for a repeat submit, seat confirmed with
 * email, seat confirmed without email.
 *
 * `eventRsvps.create` returns `confirmationEmailUnavailable` when the seat was
 * saved but the location cannot send — the guest must still hear they have a
 * seat, and must not be told to "check your email". It returns
 * `confirmationEmailQueued` when a confirmation is actually on the wire, which
 * on a repeat submit means the earlier one never left and this one is a retry.
 *
 * Already-registered is tested FIRST and beats `confirmationEmailUnavailable`:
 * that screen never promises an email, so the "we could not send" sentence
 * would only frighten a repeat visitor about a confirmation that may well have
 * been delivered.
 */
export function rsvpSuccessCopy(args: {
  alreadyRegistered: boolean;
  confirmationEmailUnavailable: boolean;
  confirmationEmailQueued?: boolean;
}): { title: string; body: string } {
  if (args.alreadyRegistered) {
    if (args.confirmationEmailQueued) {
      return {
        title: "Confirmation requested!",
        body: "A confirmation email is on its way.",
      };
    }
    return {
      title: "Already registered!",
      body: "You're all set for this event.",
    };
  }
  if (args.confirmationEmailUnavailable) {
    return {
      title: "You're in!",
      body: "Your seat is confirmed. We could not send a confirmation email — please save the event details.",
    };
  }
  return {
    title: "You're in!",
    body: "Check your email for confirmation.",
  };
}

export function RSVPModal({ event, maxPartySize = 10, open, onOpenChange }: RSVPModalProps) {
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [partySize, setPartySize] = useState<string>("1");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [alreadyRegistered, setAlreadyRegistered] = useState(false);
  const [confirmationEmailUnavailable, setConfirmationEmailUnavailable] =
    useState(false);
  const [confirmationEmailQueued, setConfirmationEmailQueued] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const resetTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const createRSVP = useMutation(api.eventRsvps.create);
  const rsvpAvailability = useQuery(
    api.eventRsvps.getAvailability,
    open ? { eventId: event._id, locationId: event.locationId } : "skip",
  );
  const isAvailabilityLoading = open && rsvpAvailability === undefined;
  const remainingGuests = rsvpAvailability?.remainingGuests;
  const hasCapacityLimit = remainingGuests !== undefined;
  const livePartyLimit = hasCapacityLimit
    ? Math.min(Math.max(remainingGuests, 0), 10)
    : maxPartySize;
  const availablePartyLimit = Math.min(Math.max(Math.floor(livePartyLimit), 0), 10);
  const availablePartyOptions = Array.from(
    { length: availablePartyLimit },
    (_, index) => index + 1,
  );
  const isEventFull = rsvpAvailability?.isFull ?? availablePartyLimit === 0;
  const seatsRemainingLabel =
    remainingGuests === 1 ? "1 seat remaining" : `${remainingGuests} seats remaining`;
  const urgentSeatsMessage =
    remainingGuests === 1
      ? "Only 1 seat left — RSVP now before the guest list closes."
      : `Only ${remainingGuests} seats left — RSVP now before the guest list closes.`;
  const capacityLimitMessage =
    isEventFull
      ? "This event is fully booked."
      : availablePartyLimit < 10
        ? availablePartyLimit === 1
          ? "Only 1 seat remains for this event."
          : `Only ${availablePartyLimit} seats remain for this event.`
        : null;

  useEffect(() => {
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (isEventFull) return;

    const partySizeNum = parseInt(partySize, 10) || 1;

    if (partySizeNum > availablePartyLimit) {
      setPartySize(String(availablePartyLimit));
    }
  }, [availablePartyLimit, isEventFull, partySize]);

  const showError = (message: string) => {
    setErrorMessage(message);
    toast.error(message);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    if (isEventFull) {
      showError("This event is fully booked.");
      return;
    }

    if (!fullName.trim() || !phone.trim() || !email.trim()) {
      showError("Please fill in your name, phone, and email");
      return;
    }

    const { firstName, lastName } = splitRsvpFullName(fullName);
    if (!firstName || !lastName) {
      showError("Please enter your first and last name");
      return;
    }

    const phoneDigits = phone.replace(/\D/g, "");
    if (phoneDigits.length < 10) {
      showError("Please enter a valid US phone number");
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showError("Please enter a valid email address");
      return;
    }

    const partySizeNum = parseInt(partySize, 10) || 1;
    if (partySizeNum > availablePartyLimit) {
      showError(
        availablePartyLimit === 1
          ? "Only 1 seat remains for this event."
          : `Only ${availablePartyLimit} seats remain for this event.`,
      );
      return;
    }

    setIsSubmitting(true);

    try {
      const result = await createRSVP({
        eventId: event._id,
        locationId: event.locationId,
        firstName,
        lastName,
        phone: phone.trim(),
        email: email.trim().toLowerCase(),
        partySize: partySizeNum,
        source: window.innerWidth < 768 ? "mobile" : "desktop",
        userAgent: navigator.userAgent,
        attribution: getLeadAttribution("event_rsvp_form"),
      });

      setIsSuccess(true);
      setAlreadyRegistered(result.alreadySubmitted ?? false);
      setConfirmationEmailUnavailable(
        result.confirmationEmailUnavailable === true,
      );
      setConfirmationEmailQueued(result.confirmationEmailQueued ?? false);
      setErrorMessage(null);
      const leadProperties = {
        lead_id: result.rsvpId,
        lead_type: "event_rsvp",
        source: "event_rsvp_form",
        event_id: event._id,
        event_type: event.eventType,
        location_id: event.locationId,
      };
      analytics.eventRsvpCompleted(event.title, leadProperties);
      analytics.leadCaptured("event_rsvp", "event_rsvp_form", leadProperties);
      setIsSubmitting(false);

      // Show success state for 2 seconds, then close
      closeTimerRef.current = setTimeout(() => {
        onOpenChange(false);
        // Reset state after closing animation
        resetTimerRef.current = setTimeout(() => {
          setFullName("");
          setPhone("");
          setEmail("");
          setPartySize("1");
          setIsSuccess(false);
          setAlreadyRegistered(false);
          setConfirmationEmailUnavailable(false);
          setConfirmationEmailQueued(false);
          setErrorMessage(null);
        }, 300);
      }, 2000);
    } catch (error) {
      console.error("RSVP error:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to RSVP";
      showError(errorMessage);
      setIsSubmitting(false);
    }
  };

  const dateStr = formatEventDate(event.date);
  const timeStr = formatTimeTo12Hour(event.startTime);
  const eventTypeLabel =
    event.eventType === "other" && event.eventTypeOther
      ? event.eventTypeOther
      : event.eventType
        ? event.eventType
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ")
        : "Event";
  const successCopy = rsvpSuccessCopy({
    alreadyRegistered,
    confirmationEmailUnavailable,
    confirmationEmailQueued,
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] gap-0 overflow-y-auto rounded-lg border border-zinc-200 bg-white p-0 text-zinc-950 shadow-[0_24px_80px_-48px_rgba(24,24,27,0.45)] sm:max-w-[420px]">
        <AnimatePresence mode="wait">
          {!isSuccess ? (
            <motion.div
              key="form"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
            >
              {/* Header */}
              <div className="border-b border-zinc-200 bg-[#fff8f4] px-6 py-5 text-zinc-950">
                <div className="mb-2 flex items-center gap-2 text-[#f4511e]">
                  <PartyPopper className="h-5 w-5" />
                  <span className="text-sm font-semibold uppercase tracking-[0.18em]">
                    {eventTypeLabel}
                  </span>
                </div>
                <DialogTitle className="text-2xl font-semibold leading-tight text-zinc-950">
                  RSVP for {event.title}
                </DialogTitle>
                <DialogDescription className="sr-only">
                  RSVP form for this event. Enter your name, phone, email, and party size to receive a confirmation.
                </DialogDescription>
              </div>

              {/* Event Info - Compact */}
              <div className="grid gap-2 border-b border-zinc-200 bg-white px-6 py-4 text-sm text-zinc-700 sm:grid-cols-3">
                <div className="flex items-center gap-1.5">
                  <Calendar className="h-4 w-4 text-[#f4511e]" />
                  <span>{dateStr.short}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock className="h-4 w-4 text-[#f4511e]" />
                  <span>{timeStr}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <MapPin className="h-4 w-4 text-[#f4511e]" />
                  <span>Las Vegas</span>
                </div>
              </div>

              {isAvailabilityLoading ? (
                <div className="border-b border-zinc-200 px-6 py-4">
                  <div className="flex items-center gap-2 rounded-md border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm text-zinc-600">
                    <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[#f4511e]" />
                    Checking seats remaining...
                  </div>
                </div>
              ) : hasCapacityLimit ? (
                <div className="border-b border-zinc-200 px-6 py-4">
                  <div
                    className={cn(
                      "rounded-md border px-4 py-3",
                      isEventFull
                        ? "border-zinc-200 bg-zinc-50"
                        : remainingGuests <= 5
                          ? "border-amber-300 bg-amber-50"
                          : "border-[#f4511e]/25 bg-[#fff8f4]",
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#f4511e]">
                          Seats remaining
                        </p>
                        <p
                          className={cn(
                            "mt-1 text-sm font-medium leading-6",
                            isEventFull
                              ? "text-zinc-600"
                              : remainingGuests <= 5
                                ? "text-amber-950"
                                : "text-zinc-700",
                          )}
                        >
                          {isEventFull
                            ? "This event is fully booked."
                            : urgentSeatsMessage}
                        </p>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        {!isEventFull && remainingGuests <= 5 ? (
                          <AlertTriangle className="h-5 w-5 text-amber-600" aria-hidden="true" />
                        ) : null}
                        <span
                          className={cn(
                            "text-3xl font-bold tabular-nums leading-none",
                            isEventFull ? "text-zinc-400" : "text-zinc-950",
                          )}
                          aria-label={seatsRemainingLabel}
                        >
                          {remainingGuests}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : null}

              {isEventFull ? (
                <div className="space-y-5 p-6">
                  <div className="rounded-md border border-zinc-200 bg-zinc-50 p-4">
                    <p className="text-sm font-semibold text-zinc-950">This event is fully booked.</p>
                    <p className="mt-1 text-sm leading-6 text-zinc-600">
                      The RSVP list is at capacity. Call us if you want to ask about cancellations.
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-12 w-full rounded-md border-zinc-300 text-base font-semibold"
                    onClick={() => onOpenChange(false)}
                  >
                    Close
                  </Button>
                </div>
              ) : !isAvailabilityLoading ? (
                <form onSubmit={handleSubmit} className="space-y-5 p-6">
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <label
                        htmlFor="rsvp-full-name"
                        className="text-sm font-semibold text-zinc-800"
                      >
                        First and last name
                      </label>
                      <Input
                        id="rsvp-full-name"
                        name="name"
                        value={fullName}
                        onChange={(e) => {
                          setFullName(e.target.value);
                          if (errorMessage) setErrorMessage(null);
                        }}
                        placeholder="First and last name"
                        autoComplete="name"
                        autoCapitalize="words"
                        disabled={isSubmitting}
                        autoFocus
                        required
                        className="min-h-[52px] rounded-md border-zinc-300 bg-white text-base text-zinc-950 placeholder:text-zinc-400 focus-visible:border-[#f4511e] focus-visible:ring-[3px] focus-visible:ring-[#f4511e]/20"
                      />
                    </div>
                    <div className="space-y-2">
                      <label
                        htmlFor="rsvp-phone"
                        className="text-sm font-semibold text-zinc-800"
                      >
                        Phone number
                      </label>
                      <Input
                        id="rsvp-phone"
                        type="tel"
                        autoComplete="tel"
                        value={phone}
                        onChange={(e) => {
                          setPhone(e.target.value);
                          if (errorMessage) setErrorMessage(null);
                        }}
                        placeholder="(702) 555-1234"
                        disabled={isSubmitting}
                        required
                        className="min-h-[52px] rounded-md border-zinc-300 bg-white text-base text-zinc-950 placeholder:text-zinc-400 focus-visible:border-[#f4511e] focus-visible:ring-[3px] focus-visible:ring-[#f4511e]/20"
                      />
                    </div>
                    <div className="space-y-2">
                      <label
                        htmlFor="rsvp-email"
                        className="text-sm font-semibold text-zinc-800"
                      >
                        Email address
                      </label>
                      <Input
                        id="rsvp-email"
                        type="email"
                        value={email}
                        onChange={(e) => {
                          setEmail(e.target.value);
                          if (errorMessage) setErrorMessage(null);
                        }}
                        placeholder="you@example.com"
                        disabled={isSubmitting}
                        required
                        className="min-h-[52px] rounded-md border-zinc-300 bg-white text-base text-zinc-950 placeholder:text-zinc-400 focus-visible:border-[#f4511e] focus-visible:ring-[3px] focus-visible:ring-[#f4511e]/20"
                      />
                    </div>

                    {/* Party Size - Inline selector */}
                    <div className="space-y-2">
                      <span className="block text-sm font-semibold text-zinc-800">
                        Party size
                      </span>
                      <div className="grid grid-cols-5 gap-2">
                        {availablePartyOptions.map((size) => (
                          <button
                            key={size}
                            type="button"
                            onClick={() => {
                              setPartySize(String(size));
                              if (errorMessage) setErrorMessage(null);
                            }}
                            disabled={isSubmitting}
                            className={`
                              min-h-[52px] rounded-md border text-sm font-semibold transition-colors duration-200 active:scale-[0.98]
                              ${partySize === String(size)
                                ? "border-[#f4511e] bg-[#f4511e] text-white"
                                : "border-zinc-300 bg-white text-zinc-700 hover:border-zinc-400 hover:bg-zinc-50"
                              }
                            `}
                          >
                            {size}
                          </button>
                        ))}
                      </div>
                      {capacityLimitMessage ? (
                        <p className="text-xs text-zinc-500">
                          {capacityLimitMessage}
                        </p>
                      ) : null}
                    </div>
                  </div>

                  {errorMessage ? (
                    <p
                      role="alert"
                      className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
                    >
                      {errorMessage}
                    </p>
                  ) : null}

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="h-12 min-h-[52px] w-full rounded-md bg-[#f4511e] text-base font-semibold text-white hover:bg-[#d94417] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:bg-[#f4511e]"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Submitting...
                      </>
                    ) : (
                      "Submit RSVP"
                    )}
                  </Button>

                  <p className="text-center text-xs text-[var(--roma-espresso)]/62">
                    We&apos;ll email you a confirmation.
                    <br />
                    <span className="text-zinc-500">
                      Your party size counts toward the event capacity.
                    </span>
                  </p>
                </form>
              ) : null}
            </motion.div>
          ) : (
            <motion.div
              key="success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              className="bg-white px-6 py-14 text-center"
            >
              <DialogTitle className="sr-only">{successCopy.title}</DialogTitle>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, damping: 15, delay: 0.1 }}
                className="mb-5"
              >
                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full border border-[#f4511e]/20 bg-[#fff8f4]">
                  <CheckCircle2 className="h-10 w-10 text-[#f4511e]" />
                </div>
              </motion.div>
              {/*
                One copy source for every outcome. Branching on
                `confirmationEmailQueued` alone told a guest at a location with
                no sending identity "Already registered!" on their FIRST RSVP,
                because a seat saved without an email also queues nothing.
              */}
              <h3 className="mb-2 text-3xl font-semibold text-zinc-950">
                {successCopy.title}
              </h3>
              <p className="text-zinc-700">{successCopy.body}</p>
            </motion.div>
          )}
        </AnimatePresence>
      </DialogContent>
    </Dialog>
  );
}
